import { useMemo } from 'react';
import { getPremiumStatus } from '@wix/ambassador-pricing-plans-v1-premium-status/http';
import { GetPremiumStatusResponse } from '@wix/ambassador-pricing-plans-v1-premium-status/types';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { ControllerFlowAPI, IHttpClient, useErrorHandler, useExperiments, useHttpClient } from '@wix/yoshi-flow-editor';

type ErrorHandler = ControllerFlowAPI['errorHandler'];

export interface PremiumApi {
  getPremiumMessageDisplayStatus(): Promise<boolean>;
  getPremiumStatus(): Promise<GetPremiumStatusResponse>;
  shouldUpgrade(): Promise<boolean>;
}

export class PremiumService implements PremiumApi {
  constructor(private httpClient: IHttpClient) {}

  async getPremiumMessageDisplayStatus() {
    const res = await this.getPremiumStatus();
    return !res?.acceptPaymentsEnabled && !res?.hasOldPlans;
  }

  async getPremiumStatus() {
    const response = await this.httpClient.request(getPremiumStatus({}));
    return response.data;
  }

  async shouldUpgrade() {
    const { acceptPaymentsEnabled, acceptPaymentsRequired } = await this.getPremiumStatus();
    return Boolean(acceptPaymentsRequired && !acceptPaymentsEnabled);
  }
}

export class PremiumServiceWithErrorHandler implements PremiumApi {
  constructor(private httpClient: IHttpClient, private errorHandler: ErrorHandler) {}

  async getPremiumMessageDisplayStatus() {
    const res = await this.getPremiumStatus();
    return !res?.acceptPaymentsEnabled && !res?.hasOldPlans;
  }

  async getPremiumStatus() {
    const response = await this.errorHandler.withErrorHandler(() => this.httpClient.request(getPremiumStatus({})), {
      errorCodesMap: {},
    });
    return response.data;
  }

  async shouldUpgrade() {
    const { acceptPaymentsEnabled, acceptPaymentsRequired } = await this.getPremiumStatus();
    return Boolean(acceptPaymentsRequired && !acceptPaymentsEnabled);
  }
}

export function createPremiumApi(flowAPI: ControllerFlowAPI) {
  const isErrorHandlerEnabled = flowAPI.experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA);
  if (isErrorHandlerEnabled) {
    return new PremiumServiceWithErrorHandler(flowAPI.httpClient, flowAPI.errorHandler);
  }
  return new PremiumService(flowAPI.httpClient);
}

export const usePremiumApi = () => {
  const { experiments } = useExperiments();
  const httpClient = useHttpClient();
  const errorHandler = useErrorHandler();

  const isErrorHandlerEnabled = experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA);

  const premiumApi = useMemo(() => {
    if (isErrorHandlerEnabled) {
      return new PremiumServiceWithErrorHandler(httpClient, errorHandler);
    }

    return new PremiumService(httpClient);
  }, [errorHandler, httpClient, isErrorHandlerEnabled]);

  return premiumApi;
};
